import { html, render } from 'lit-html';
import { appStore, actions } from '../store/appStore.js';
import { APIRoutePrefixWithStatus, APIRouteWithStatus } from '../types/index.js';
import { formatLocalTime, formatSimpleDateTime } from '../utils/dateTime.js';
import { api, ApiError } from '../services/api.js';
import { dataService } from '../services/dataService.js';
import { audioNotificationService } from '../services/audioNotificationService.js';
import { ErrorCode } from '../constants/errorCodes.js';
import { barcodeScanner } from '../services/barcodeScanner.js';
import chevronRightIcon from '../icons/chevron-right.svg';

export class RoutesList extends HTMLElement {
  private unsubscribe?: () => void;
  private loadingRoutes: boolean = false;
  private loadingDeliveries: boolean = false;

  connectedCallback() {
    this.unsubscribe = appStore.subscribe(() => {
      this.render();
    });
    this.render();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  private getStatusBadgeClass(status: APIRoutePrefixWithStatus['status']): string {
    const baseClass = 'status-badge';
    switch (status) {
      case 'in_progress':
        return `${baseClass} in-progress`;
      case 'incomplete':
        return `${baseClass} incomplete`;
      case 'completed':
        return `${baseClass} complete`;
      case 'not_started':
        return `${baseClass} not-started`;
      default:
        return `${baseClass} not-started`;
    }
  }

  private renderBadges = (flags: { is_allergen?: boolean; is_pack_threshold?: boolean; is_delivery_threshold?: boolean }) => {
    const items = [] as any[];
    if (flags?.is_allergen) {
      items.push(html`<span class="text-red-500 font-semibold">A</span>`);
    }
    if (flags?.is_pack_threshold) {
      items.push(html`<span class="text-purple-500 font-semibold">PT</span>`);
    }
    if (flags?.is_delivery_threshold) {
      items.push(html`<span class="text-orange-500 font-semibold">DT</span>`);
    }
    return html`<span class="inline-flex items-center gap-2 ml-2 whitespace-nowrap">${items}</span>`;
  }

  private getStatusText(status: APIRoutePrefixWithStatus['status']): string {
    switch (status) {
      case 'in_progress':
        return 'IN PROGRESS';
      case 'incomplete':
        return 'INCOMPLETE';
      case 'completed':
        return 'COMPLETE';
      case 'not_started':
        return 'NOT STARTED';
      default:
        return 'NOT STARTED';
    }
  }

  private toggleRoutePrefix = async (event: Event, routePrefix: APIRoutePrefixWithStatus) => {
    event.preventDefault();
    event.stopPropagation();

    const state = appStore.getState();

    if (state.expandedRoutePrefix?.routePrefixId === routePrefix.route_prefix_id) {
      // Collapse if already expanded
      actions.setExpandedRoutePrefix(null, null);
      actions.setExpandedRoute(null);
    } else {
      // Expand new row and fetch routes
      if (state.currentCycle) {
        actions.setExpandedRoutePrefix(routePrefix.route_prefix_id, state.currentCycle.id);
      }
      actions.setExpandedRoute(null);
      this.loadingRoutes = true;
      this.render();

      try {
        if (state.currentCycle && state.selectedFacility) {
          const routes = await api.getRoutesByRoutePrefix(
            state.currentCycle.id,
            routePrefix.route_prefix_id,
            state.selectedFacility.id
          );
          actions.setExpandedRoutesData(routes);
        }
      } catch (error) {
        console.error('Failed to fetch routes:', error);
        actions.addAlert('Failed to fetch routes', 'error');
        actions.setExpandedRoutesData([]);
      } finally {
        this.loadingRoutes = false;
      }
    }
    this.render();
  };

  private toggleRoute = async (event: Event, route: APIRouteWithStatus) => {
    event.preventDefault();
    event.stopPropagation();

    const state = appStore.getState();

    if (state.expandedRoute?.routeId === route.id) {
      // Collapse if already expanded
      actions.setExpandedRoute(null);
    } else {
      // Expand new route and fetch deliveries
      actions.setExpandedRoute(route.id);
      this.loadingDeliveries = true;
      this.render();

      try {
        const deliveries = await api.getDeliveriesByRouteId(route.id);
        actions.setExpandedDeliveriesData(deliveries);
      } catch (error) {
        console.error('Failed to fetch deliveries:', error);
        actions.addAlert('Failed to fetch deliveries', 'error');
        actions.setExpandedDeliveriesData([]);
      } finally {
        this.loadingDeliveries = false;
      }
    }
    this.render();
  };

  private formatPackedStatus(status: string): string {
    if (status === 'manual_entry') {
      return 'Manual entry';
    }
    return status;
  }

  private getLinkedPackLine = () => {
    const state = appStore.getState();
    const packLine = state.linkedPackLine;
    if (!packLine) {
      actions.addAlert("No line found. Please scan an active line.", "error");
      return;
    }
    return packLine;
  }

  private handleErrorForMarkAs = (error: unknown) => {
    // Handle specific API errors using error codes
    if (error instanceof ApiError && error.code) {
      audioNotificationService.playErrorSound();
      switch (error.code) {
        case ErrorCode.PACK_SESSION_NOT_STARTED:
          actions.addAlert('No line found. Please scan an active line.', 'error');
          break;
        case ErrorCode.PACK_LINE_HAS_ENDED:
          actions.addAlert('Pack line has ended. Please scan an active pack line.', 'error');
          barcodeScanner.handlePackLineHasEnded();
          break;
        case ErrorCode.PACK_LINE_ON_BREAK:
          actions.addAlert('Line is on break. Please end the pack line break before resuming.', 'error');
          break;
        default:
          actions.addAlert('Failed to edit delivery. Please try again.', 'error');
      }
    } else {
      audioNotificationService.playErrorSound();
      actions.addAlert('Failed to edit delivery. Please try again.', 'error');
    }
  }

  private handleMarkAsPacked = async (event: Event, deliveryId: number) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const packLine = this.getLinkedPackLine();
      if (!packLine) {
        return;
      }
      const lineLeadName = packLine.lineLeadName || '';
      const packLineId = packLine.id;

      await dataService.updateDeliveryStatus(
        deliveryId,
        'manual_entry',
        lineLeadName,
        packLineId
      );
      // Data refresh is now handled automatically by dataService
    } catch (error) {
      this.handleErrorForMarkAs(error);
      console.error('Failed to mark delivery as packed:', error);
    }
  };

  private handleUndoMarkAsPacked = async (event: Event, deliveryId: number) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const packLine = this.getLinkedPackLine();
      if (!packLine) {
        return;
      }
      const lineLeadName = packLine.lineLeadName || '';
      const packLineId = packLine.id;

      await dataService.updateDeliveryStatus(
        deliveryId,
        'pending',
        lineLeadName,
        packLineId
      );
      // Data refresh is now handled automatically by dataService
    } catch (error) {
      this.handleErrorForMarkAs(error);
      console.error('Failed to undo mark as packed:', error);
    }
  };

  private renderDeliveryRow = (delivery: any) => {
    const rowClass = delivery.status !== 'pending' ? 'bg-green-50' : '';
    const state = appStore.getState();
    const hasLinkedPackLine = !!state.linkedPackLine;

    return html`
      <tr class="${rowClass}">
        <td class="px-4 py-3 text-sm font-medium text-gray-900">${delivery.drop_number}</td>
        <td class="px-4 py-1.5">
          ${hasLinkedPackLine ? html`
            ${delivery.status === 'pending' ? html`
              <button
                type="button"
                @click=${(event: Event) => this.handleMarkAsPacked(event, delivery.id)}
                class="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded"
              >
                Mark Packed
              </button>
            ` : delivery.status === 'manual_entry' && delivery.status_submitted_by ? html`
              <button
                type="button"
                @click=${(event: Event) => this.handleUndoMarkAsPacked(event, delivery.id)}
                class="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
              >
                Unmark Packed
              </button>
            ` : ''}
          ` : ''}
        </td>
        <td class="px-4 py-3 text-sm text-gray-900">
          <div class="flex items-center gap-2 whitespace-nowrap">
            <span>${delivery.recipient_name || 'N/A'}</span>
            ${this.renderBadges(delivery)}
          </div>
        </td>
        <td class="px-4 py-3 text-sm">
          <span class="capitalize ${delivery.status === 'scanned' ? 'text-green-700 font-medium' : 'text-gray-600'}">
            ${this.formatPackedStatus(delivery.status)}
          </span>
        </td>
        <td class="px-4 py-3 text-sm text-gray-900">${delivery.expected_bag_count || 0}</td>
        <td class="px-4 py-3 text-sm text-gray-600">${delivery.status_submitted_at ? formatLocalTime(delivery.status_submitted_at) : 'N/A'}</td>
      </tr>
    `;
  };

  private renderRouteRow = (route: APIRouteWithStatus) => {
    const state = appStore.getState();
    const isExpanded = state.expandedRoute?.routeId === route.id;
    const expandedDeliveries = state.expandedDeliveriesData

    return html`
      <div class="border-b border-gray-200">
        <div
          class="grid grid-cols-9 gap-4 items-center text-sm p-4 ${isExpanded ? 'bg-blue-100' : 'bg-blue-50 hover:bg-blue-100'} cursor-pointer"
          @click=${(event: Event) => this.toggleRoute(event, route)}
        >
          <div class="font-medium text-gray-900 ml-5">
            <div class="flex items-center gap-2 whitespace-nowrap">
              <span>${route.name}</span>
              ${this.renderBadges(route)}
            </div>
          </div>
          <div class="text-gray-600"></div>
          <div class="text-gray-600">${route.total_deliveries}</div>
          <div class="text-gray-600">${route.packed_deliveries}</div>
          <div class="text-gray-600">${formatSimpleDateTime(route.started_at)}</div>
          <div class="text-gray-600">${formatSimpleDateTime(route.ended_at)}</div>
          <div class="whitespace-nowrap">
            <span class="${this.getStatusBadgeClass(route.status)}">
              ${this.getStatusText(route.status)}
            </span>
          </div>
          <div class="font-medium text-gray-900">${Math.round(route.percent_completed)}%</div>
          <div class="flex justify-end">
            <img
              src="${chevronRightIcon}"
              class="w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-90' : ''}"
              alt="Expand"
            />
          </div>
        </div>

        ${isExpanded ? html`
          <div class="bg-gray-50">
            ${this.loadingDeliveries ? html`
              <div class="p-4 text-center text-gray-500">Loading deliveries...</div>
            ` : expandedDeliveries.length > 0 ? html`
              <table class="w-full">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Drop #</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Manual Actions</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Packed</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bag Count</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pack Time</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  ${expandedDeliveries.map(delivery => this.renderDeliveryRow(delivery))}
                </tbody>
              </table>
            ` : html`
              <div class="p-4 text-center text-gray-500">No deliveries found</div>
            `}
          </div>
        ` : ''}
      </div>
    `;
  };

  private renderRoutePrefix = (routePrefix: APIRoutePrefixWithStatus) => {
    const state = appStore.getState();
    const isExpanded = state.expandedRoutePrefix?.routePrefixId === routePrefix.route_prefix_id;
    let expandedRoutes = this.sortRoutes(state.expandedRoutesData);

    return html`
      <div>
        <div
          class="route-row ${routePrefix.status} ${isExpanded ? 'bg-gray-200' : 'bg-white'} p-4 hover:bg-gray-50 transition-colors cursor-pointer"
          @click=${(event: Event) => this.toggleRoutePrefix(event, routePrefix)}
        >
          <div class="grid grid-cols-9 gap-4 items-center text-sm">
            <div class="font-medium text-gray-900">${routePrefix.route_prefix}</div>
            <div class="text-gray-600">${routePrefix.total_routes}</div>
            <div class="text-gray-600">${routePrefix.total_deliveries}</div>
            <div class="text-gray-600">${routePrefix.packed_deliveries}</div>
            <div class="text-gray-600">${formatSimpleDateTime(routePrefix.started_at)}</div>
            <div class="text-gray-600">${formatSimpleDateTime(routePrefix.ended_at)}</div>
            <div class="whitespace-nowrap">
              <span class="${this.getStatusBadgeClass(routePrefix.status)}">
                ${this.getStatusText(routePrefix.status)}
              </span>
            </div>
            <div class="font-medium text-gray-900">${Math.round(routePrefix.percent_completed)}%</div>
            <div class="flex justify-end">
              <img
                src="${chevronRightIcon}"
                class="w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-90' : ''}"
                alt="Expand"
              />
            </div>
          </div>
        </div>

        ${isExpanded ? html`
          <div class="bg-gray-50">
            ${this.loadingRoutes ? html`
              <div class="p-4 text-center text-gray-500">Loading routes...</div>
            ` : expandedRoutes.length > 0 ? html`
              <div>
                ${expandedRoutes.map(route => this.renderRouteRow(route))}
              </div>
            ` : html`
              <div class="p-4 text-center text-gray-500">No routes found</div>
            `}
          </div>
        ` : ''}
      </div>
    `;
  };

  private sortRoutePrefixes(routePrefixes: APIRoutePrefixWithStatus[]): APIRoutePrefixWithStatus[] {
    return [...routePrefixes].sort((a, b) => {
      // Define status priority: incomplete, then in progress then completed, then not_started
      const statusPriority = {
        'incomplete': 0,
        'in_progress': 1,
        'completed': 2,
        'not_started': 3
      };

      const aStatusPriority = statusPriority[a.status] ?? 5;
      const bStatusPriority = statusPriority[b.status] ?? 5;

      // First sort by status priority
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority;
      }

      // Then sort by start time (most recent first)
      const aStartTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bStartTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      if (aStartTime !== bStartTime) {
        return bStartTime - aStartTime; // Descending order (most recent first)
      }

      // Finally sort alphabetically by route_prefix within the same status and time
      return a.route_prefix.localeCompare(b.route_prefix);
    });
  }

  private sortRoutes(routes: APIRouteWithStatus[]): APIRouteWithStatus[] {
    return [...routes].sort((a, b) => {
      const statusPriority = {
        'incomplete': 0,
        'in_progress': 1,
        'completed': 2,
        'not_started': 3
      };

      const aStatusPriority = statusPriority[a.status] ?? 5;
      const bStatusPriority = statusPriority[b.status] ?? 5;

      // First sort by status priority
      if (aStatusPriority !== bStatusPriority) {
        return aStatusPriority - bStatusPriority;
      }

      // Then sort by start time (most recent first)
      const aStartTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bStartTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      if (aStartTime !== bStartTime) {
        return bStartTime - aStartTime; // Descending order (most recent first)
      }

      // Finally sort alphabetically by name within the same status and time
      return a.name.localeCompare(b.name);
    });
  }

  private render() {
    const state = appStore.getState();
    const { routePrefixes, isPackLinesExpanded } = state;

    if (isPackLinesExpanded) {
      render(html``, this);
      return;
    }

    // Sort routes by status (in_progress first) then alphabetically
    const sortedRoutePrefixes = this.sortRoutePrefixes(routePrefixes);

    const template = html`
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Route Prefixes</h2>
          <div class="grid grid-cols-9 gap-4 text-xs font-medium text-gray-500">
            <div>Route Group</div>
            <div>Routes</div>
            <div>Drops</div>
            <div>Scanned</div>
            <div>Start</div>
            <div>End</div>
            <div>Status</div>
            <div>Completion</div>
            <div></div>
          </div>
        </div>
        <div class="divide-y divide-gray-200">
          ${sortedRoutePrefixes.map(this.renderRoutePrefix)}
        </div>
      </div>
    `;

    render(template, this);
  }
}

customElements.define('routes-list', RoutesList);
